import React, { useState, useEffect, useCallback } from 'react';
import { format } from 'date-fns';
import {
  ExternalLink,
  Trash2,
  Pencil,
  Save,
  X
} from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { apiService } from '../../services/api';
import { useSearch } from '../../contexts/SearchContext';
import wsService from '../../services/wsService';
import DeleteConfirmPopover from '../shared/DeleteConfirmPopover';
import './MyKnowledgeEntries.css';

/**
 * EntryGroup
 * Renders one entry as a <tbody> with two <tr>s — text row on top, icon row
 * directly below. `table-layout: fixed` guarantees both rows share column
 * widths, so icons in the Title and Thoughts columns line up automatically
 * with the text above them.
 *
 * Visually the pair is meant to read as a single entry: no border, no
 * background change, no hover separator between the text row and its
 * companion icon row. The inter-entry divider comes from the icon row's
 * border-bottom (see MyKnowledgeEntries.css). Multiple <tbody>s in one
 * <table> is valid HTML; this is a layout-only mechanism, invisible to
 * the end user.
 *
 * Props:
 *   entry                 — the knowledge entry
 *   onDeleteEntry         — (entryId, anchorEl) => void   (opens the confirm popover)
 *   onDeleteThought       — (entryId, anchorEl) => void   (opens the confirm popover)
 *   onEditThought         — (entry) => void               (opens the thought-editor modal)
 *   onPromptPersonalTags  — (entry) => void               (Yes: opens the personal-tags modal)
 *   onDeclinePersonalTags — (entryId) => void             (No: saves the empty "No" state)
 *
 * Personal-tags prompt: when entry.personalTags === null the user has never
 * been prompted, so the Thoughts text cell shows a small "add tags?" box with
 * Yes/No instead of the thought. The icon row (edit/delete) is unaffected and
 * behaves exactly as it does on extension-captured entries with no thought.
 */
const EntryGroup = ({
  entry,
  onDeleteEntry,
  onDeleteThought,
  onEditThought,
  onPromptPersonalTags,
  onDeclinePersonalTags
}) => {
  return (
    <tbody className="entry-group">
      <tr className="entry-text-row">
        <td className="col-added">
          {entry.createdAt
            ? format(new Date(entry.createdAt), 'MM/dd/yyyy')
            : 'No date'}
        </td>
        <td className="col-title">
          <div className="cell-content">
            {entry.document?.docTitle || 'Untitled'}
          </div>
        </td>
        <td className="col-focus">
          <div className="cell-content">
            {entry.focus?.focus || ''}
          </div>
        </td>
        <td className="col-thoughts">
          <div className="cell-content">
            {entry.personalTags === null ? (
              <div className="personal-tags-prompt">
                <p className="personal-tags-prompt-text">
                  Would you like to add tags to better align this entry with your thinking?
                </p>
                <div className="personal-tags-prompt-actions">
                  <button
                    type="button"
                    className="personal-tags-prompt-button"
                    onClick={() => onPromptPersonalTags(entry)}
                  >
                    Yes
                  </button>
                  <button
                    type="button"
                    className="personal-tags-prompt-button"
                    onClick={() => onDeclinePersonalTags(entry._id)}
                  >
                    No
                  </button>
                </div>
              </div>
            ) : (
              entry.uThought || ''
            )}
          </div>
        </td>
      </tr>
      <tr className="entry-icon-row">
        <td className="col-added" />
        <td className="col-title">
          <div className="icon-container">
            {entry.document?.docUrl && (
              <a
                href={entry.document.docUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="icon-button url-button"
                title="Open document"
              >
                <ExternalLink size={20} />
              </a>
            )}
            <button
              className="icon-button"
              onClick={(e) => onDeleteEntry(entry._id, e.currentTarget)}
              title="Delete entry"
            >
              <Trash2 size={20} />
            </button>
          </div>
        </td>
        <td className="col-focus" />
        <td className="col-thoughts">
          <div className="icon-container">
            <button
              className="icon-button"
              onClick={() => onEditThought(entry)}
              title="Edit thought"
            >
              <Pencil size={20} />
            </button>
            <button
              className="icon-button"
              onClick={(e) => onDeleteThought(entry._id, e.currentTarget)}
              title="Delete thought"
            >
              <Trash2 size={20} />
            </button>
          </div>
        </td>
      </tr>
    </tbody>
  );
};

  const MyKnowledgeEntries = ({
    searchResults,
    selectedTags, 
    selectedSources,
    sourceCorrection,
    unidentifiedSources,
    onSourceCorrection 
  }) => {
    console.debug('MyKnowledgeEntries props received:', {
      hasSearchResults: !!searchResults,
      searchResultsLength: searchResults?.entries?.length,
      selectedSourcesLength: selectedSources?.length
    });

  // =========================================
  // State Management
  // =========================================
  const { isAuthenticated, isLoading: authLoading } = useAuth();
  const [entries, setEntries] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [editingThought, setEditingThought] = useState(null);
  const [thoughtText, setThoughtText] = useState('');
  // Personal-tags modal (the Yes path). `personalTagsEntryId` is the _id of the
  // entry being tagged, or null when the modal is closed; `personalTagsText` is
  // the textarea contents. Started empty — the modal never shows focus tags.
  const [personalTagsEntryId, setPersonalTagsEntryId] = useState(null);
  const [personalTagsText, setPersonalTagsText] = useState('');
  // Delete-confirmation popover state: { entryId, type, anchor } or null.
  // Populated by the two trash-icon onClick handlers; cleared by the popover.
  const [pendingDelete, setPendingDelete] = useState(null);

  // Action-failure banner. Deliberately NOT the `error` state: `error` is a
  // full-page replacement (see the `if (error) return …` guard below), and
  // blanking the list is the wrong response to a failed delete — the user needs
  // to see the list to judge whether the entry actually went. This state is also
  // immune to the `setError(null)` calls that begin every load path, which would
  // otherwise wipe the message during the post-failure refresh.
  const [actionError, setActionError] = useState(null);

  // Get search context. executeSearch re-runs the current query against the
  // server; the effect below mirrors the refreshed results into `entries`.
  const { results, query, isSearching, currentPage: contextPage, setPage, executeSearch } = useSearch();

  // =========================================
  // Filter Functions
  // =========================================
  const filterBySource = (entries, sources) => {
    if (!sources.length) return entries;
    return entries.filter(entry => {
      try {
        const url = new URL(entry.document.docUrl);
        const domain = url.hostname.replace(/^www\./, '');
        return sources.includes(domain);
      } catch (error) {
        console.warn('Error parsing URL:', entry.document.docUrl);
        return false;
      }
    });
  };

  // =========================================
  // Data Loading
  // =========================================
  const loadEntries = useCallback(async (page) => {
    setIsLoading(true);
    setError(null);
    try {
      // Handle search case
      if (query && results) {
        console.debug('Setting search results:', {
          hasResults: !!results?.entries,
          resultCount: results?.entries?.length,
          page
        });
        setEntries(results.entries || []);
        setTotalPages(results.totalPages || 1);
        return;
      }

      console.debug('Loading entries:', {
        page,
        selectedSources,
        isSearch: !!query
      });
    
      const response = await apiService.knowledge.getEntries(page);
      console.debug('Entries response received:', {
        hasEntries: !!response?.entries,
        entryCount: response?.entries?.length,
        totalPages: response?.totalPages
      });
    
      if (response?.entries) {
        console.debug('Setting entries:', {
          firstEntry: response.entries[0],
          totalEntries: response.entries.length
        });
        setEntries(response.entries);
        setTotalPages(response.totalPages || 1);
      } else {
        console.debug('No entries in response, clearing state');
        setEntries([]);
        setTotalPages(1);
      }   
    } catch (err) {
      console.error('Error fetching entries:', {
        error: err,
        message: err.response?.data?.message,   
        page
      });
      setError(err.response?.data?.message || 'Failed to load entries');
    } finally {
      setIsLoading(false);
    }
  }, [selectedSources, query, results]);
          
  useEffect(() => {
    // Don't fetch if not authenticated or auth is still loading
    if (!isAuthenticated || authLoading) {
      setIsLoading(false);
      return;
    }

    const loadData = async () => {
      setIsLoading(true);
      setError(null);
      try {
        // Handle search case
        if (query && results) {
          console.debug('Setting search results:', {
            hasResults: !!results?.entries,
            resultCount: results?.entries?.length,
            selectedSources,
            page: contextPage
          });
          setEntries(results.entries || []);
          setTotalPages(results.totalPages || 1);
          return;
        }

        console.debug('Loading entries:', {
          page: currentPage,
          selectedSources,
          isSearch: !!query
        });

        // If sources are selected, use them in the API call
        const response = await apiService.knowledge.getEntries(
          currentPage,   
          selectedSources.length ? { sources: selectedSources } : {}
        );

        console.debug('Entries response received:', {
          hasEntries: !!response?.entries,      
          entryCount: response?.entries?.length,
          totalPages: response?.totalPages
        });

        setEntries(response?.entries || []);
        setTotalPages(response?.totalPages || 1);
      } catch (err) {
        console.error('Error fetching entries:', {
          error: err,
          message: err.response?.data?.message,
          page: currentPage
        });
        setError(err.response?.data?.message || 'Failed to load entries');
      } finally {
        setIsLoading(false);
      }
    };

    loadData();
  }, [currentPage, selectedSources, query, results, contextPage, isAuthenticated, authLoading]);

  // Refresh the list when the backend signals a new capture for this
  // user. The WebSocket carries no content — only the `type` signal.
  // All data still comes through the existing authenticated HTTP
  // endpoint via loadEntries().
  useEffect(() => {
    const handler = () => { loadEntries(currentPage); };
    wsService.subscribe('mke_updated', handler);
    return () => wsService.unsubscribe('mke_updated', handler);
  }, [loadEntries, currentPage]);

  // =========================================
  // Event Handlers
  // =========================================

  /**
   * Reconcile the list against the server after a mutation.
   *
   * loadEntries() short-circuits while a search is active (it mirrors the
   * SearchContext snapshot rather than fetching), so calling it after a delete
   * is a no-op in that state and the list can keep showing a row that no longer
   * exists. Re-running the search against the server is the correct refresh
   * there; executeSearch updates SearchContext.results, and the effect above
   * mirrors the fresh results into `entries`.
   */
  const refreshAfterMutation = useCallback(async () => {
    if (query) {
      await executeSearch(query, contextPage || 1);
    } else {
      await loadEntries(currentPage);
    }
  }, [query, contextPage, executeSearch, loadEntries, currentPage]);

  /**
   * Message for a failed mutation. A 500 is the ambiguous case: the write may
   * have committed server-side before the response failed, so we tell the user
   * to trust the refreshed list rather than asserting the operation failed.
   */
  const mutationFailureMessage = (error, noun) => {
    const status = error.response?.status;
    if (status === 500) {
      return `The server reported an error while deleting ${noun}. The list has been refreshed — check whether the change took effect.`;
    }
    if (status === 404) {
      return `That ${noun} no longer exists. The list has been refreshed.`;
    }
    return `Failed to delete ${noun}. The list has been refreshed — please try again.`;
  };

  const handleDeleteEntry = async (_id) => {
    setActionError(null);
    try {
      await apiService.knowledge.deleteEntry(_id);
      await refreshAfterMutation();
    } catch (error) {
      // Reconcile FIRST, then report. Every load path opens with setError(null)
      // and the search effect re-runs on results change, so a message set before
      // the refresh would be wiped by it.
      try {
        await refreshAfterMutation();
      } catch (refreshErr) {
        console.error('Refresh after failed delete also failed:', refreshErr);
      }
      setActionError(mutationFailureMessage(error, 'this entry'));
    }
  };

  const handleDeleteThought = async (_id) => {
    setActionError(null);
    try {
      await apiService.knowledge.updateThought(_id, '');  // Set thought to empty string
      await refreshAfterMutation();
    } catch (error) {
      try {
        await refreshAfterMutation();
      } catch (refreshErr) {
        console.error('Refresh after failed thought delete also failed:', refreshErr);
      }
      setActionError(mutationFailureMessage(error, 'this thought'));
    }
  };

  const handleEditThought = (entry) => {
    setEditingThought(entry._id);  // Use _id
    setThoughtText(entry.uThought || '');
  };

  // EntryGroup callbacks — open the delete-confirm popover anchored to the
  // clicked trash icon. The actual delete runs only when the user confirms.
  const handleRequestDeleteEntry = useCallback((entryId, anchor) => {
    setPendingDelete({ entryId, type: 'entry', anchor });
  }, []);

  const handleRequestDeleteThought = useCallback((entryId, anchor) => {
    setPendingDelete({ entryId, type: 'thought', anchor });
  }, []);

  const handleSaveThought = async () => {
    try {
      await apiService.knowledge.updateThought(editingThought, thoughtText);
    
      setEditingThought(null);
      setThoughtText('');
      setError(null);
    
      // Force refresh of entries
      await loadEntries(currentPage);
    } catch (error) {
      console.error('Error updating thought:', error);
      // Only show error if the thought update actually failed
      if (error.response?.status !== 500) {
        setError('Failed to update thought');
      } else {
        // If it's a 500 error, still try to refresh the entries
        await loadEntries(currentPage);
        setEditingThought(null);
        setThoughtText('');
      }
    }
  };

  // Personal-tags prompt handlers (EntryGroup callbacks).
  // No path: save the empty "No" state so the prompt won't reappear.
  const handleDeclinePersonalTags = useCallback(async (entryId) => {
    try {
      await apiService.knowledge.savePersonalTags(entryId, '');
      await loadEntries(currentPage);  // Refresh: personalTags is now non-null
    } catch (error) {
      if (error.response?.status !== 500) {
        setError('Failed to save tag preference');
      } else {
        await loadEntries(currentPage);
      }
    }
  }, [loadEntries, currentPage]);

  // Yes path: open the tag-entry modal. Start empty — never prefill focus tags.
  const handlePromptPersonalTags = useCallback((entry) => {
    setPersonalTagsEntryId(entry._id);
    setPersonalTagsText('');
  }, []);

  // Cancel: close the modal without calling the endpoint. personalTags stays
  // null, so the prompt reappears (here immediately, and on the next visit).
  const handleCancelPersonalTags = useCallback(() => {
    setPersonalTagsEntryId(null);
    setPersonalTagsText('');
  }, []);

  const handleSavePersonalTags = async () => {
    try {
      await apiService.knowledge.savePersonalTags(personalTagsEntryId, personalTagsText);
      setPersonalTagsEntryId(null);
      setPersonalTagsText('');
      setError(null);
      await loadEntries(currentPage);  // Refresh: prompt disappears
    } catch (error) {
      console.error('Error saving personal tags:', error);
      if (error.response?.status !== 500) {
        setError('Failed to save tags');
      } else {
        await loadEntries(currentPage);
        setPersonalTagsEntryId(null);
        setPersonalTagsText('');
      }
    }
  };

  // =========================================
  // Render Methods
  // =========================================
  if (error) {
    return <div className="error-message">{error}</div>;
  }

  console.debug('Entries before local filtering:', {
    entries: entries,
    selectedSources: selectedSources,
    entriesURLs: entries?.map(e => e.document?.docUrl)
  });
    
  const filteredEntries = filterBySource(entries, selectedSources);
  
  console.debug('Pre-render state:', {
    originalEntries: entries?.length || 0,
    filteredEntries: filteredEntries?.length || 0,
    selectedSources,
    isLoading,
    isSearching
  });

  return (
    <div className="knowledge-entries">
      {/* Action-failure banner. Sits above the list rather than replacing it —
          after a failed delete the user needs the list visible to see what
          actually happened. Dismissible; also cleared on the next attempt. */}
      {actionError && (
        <div className="action-error" role="alert">
          <span className="action-error-text">{actionError}</span>
          <button
            type="button"
            className="action-error-dismiss"
            onClick={() => setActionError(null)}
            aria-label="Dismiss message"
          >
            ×
          </button>
        </div>
      )}
      <div className="results-container">
        <div className="table-wrapper">
          <table className="results-list">
            <thead>
              <tr>
                <th className="col-added">Added</th>
                <th className="col-title">Title</th>
                <th className="col-focus">My Focus</th>
                <th className="col-thoughts">My Thoughts</th>
              </tr>
            </thead>
            {/* Multiple <tbody>s in one <table> is valid HTML. Each entry is
                its own <tbody> (EntryGroup) containing a text row + icon row;
                loading / empty / no-results states render as a standalone
                <tbody> so nothing ever nests. */}
            {isLoading || isSearching ? (
              <tbody>
                <tr>
                  <td colSpan="4" className="loading-message">
                    {isSearching ? 'Searching...' : 'Loading entries...'}
                  </td>
                </tr>
              </tbody>
            ) : filteredEntries.length === 0 ? (
              <tbody>
                <tr>
                  <td colSpan="4" className="no-entries">
                    {query ? 'No matching entries found.' : 'No entries found.'}
                  </td>
                </tr>
              </tbody>
            ) : (
              filteredEntries.map((entry) => (
                <EntryGroup
                  key={entry._id}
                  entry={entry}
                  onDeleteEntry={handleRequestDeleteEntry}
                  onDeleteThought={handleRequestDeleteThought}
                  onEditThought={handleEditThought}
                  onPromptPersonalTags={handlePromptPersonalTags}
                  onDeclinePersonalTags={handleDeclinePersonalTags}
                />
              ))
            )}
          </table>
        </div>

        {/* Delete-confirmation popover (non-modal). Sibling of .table-wrapper
            so it isn't clipped by overflow: auto; uses position: fixed. */}
        <DeleteConfirmPopover
          pendingDelete={pendingDelete}
          onDismiss={() => setPendingDelete(null)}
          onConfirm={() => {
            if (!pendingDelete) return;
            const { entryId, type } = pendingDelete;
            setPendingDelete(null);
            if (type === 'entry') {
              handleDeleteEntry(entryId);
            } else {
              handleDeleteThought(entryId);
            }
          }}
          message={
            pendingDelete?.type === 'entry'
              ? 'Delete this entry? This removes the title, focus, and your thought.'
              : 'Delete just your thought? The title and focus will remain.'
          }
        />

        {/* Thought Editor Modal */}
        {editingThought && (
          <>
            <div className="thought-editor-backdrop" onClick={() => setEditingThought(null)} />
            <div className="thought-editor">
              <textarea
                value={thoughtText}
                onChange={(e) => setThoughtText(e.target.value)}
                placeholder="Enter your thoughts..."
              />
              <div className="button-container">
                <button 
                  className="icon-button" 
                  onClick={handleSaveThought}
                  title="Save thought"
                >
                  <Save size={20} />
                </button>
                <button 
                  className="icon-button" 
                  onClick={() => setEditingThought(null)}
                  title="Cancel"
                >
                  <X size={20} />
                </button>
              </div>
            </div>
          </>
        )}

        {/* Personal Tags Modal (Yes path). Single textarea, n/100 counter,
            Save + Cancel. Never displays the entry's focus tags. */}
        {personalTagsEntryId && (
          <>
            <div className="thought-editor-backdrop" onClick={handleCancelPersonalTags} />
            <div className="thought-editor personal-tags-modal">
              <h3 className="personal-tags-modal-title">Add tags to this entry</h3>
              <textarea
                className="personal-tags-textarea"
                value={personalTagsText}
                onChange={(e) => setPersonalTagsText(e.target.value)}
                maxLength={100}
                placeholder="Separate your tags with commas..."
                autoFocus
              />
              <div className="personal-tags-char-count">
                {personalTagsText.length}/100 characters
              </div>
              <div className="personal-tags-modal-actions">
                <button
                  type="button"
                  className="personal-tags-modal-button save"
                  onClick={handleSavePersonalTags}
                >
                  Save
                </button>
                <button
                  type="button"
                  className="personal-tags-modal-button cancel"
                  onClick={handleCancelPersonalTags}
                >
                  Cancel
                </button>
              </div>
            </div>
          </>
        )}

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="pagination">
            <button
              onClick={() => {
                if (query) {
                  // Use context's setPage for search results
                  setPage(Math.max(1, contextPage - 1));
                } else {
                  // Use local pagination for regular entries
                  setCurrentPage(p => Math.max(1, p - 1));
                }
              }}
              disabled={(query ? contextPage : currentPage) === 1 || isLoading || isSearching}
            >
              Previous
            </button>
            <span>Page {query ? contextPage : currentPage} of {totalPages}</span>
            <button
              onClick={() => {
                if (query) {
                  // Use context's setPage for search results
                  setPage(Math.min(totalPages, contextPage + 1));
                } else {
                  // Use local pagination for regular entries
                  setCurrentPage(p => Math.min(totalPages, p + 1));
                }
              }}
              disabled={(query ? contextPage : currentPage) === totalPages || isLoading || isSearching}
            >
              Next
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default MyKnowledgeEntries;
